import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TabList,
  Tab,
  Spinner,
  Text,
  Button,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Copy16Regular, ArrowMaximize20Regular, ArrowMinimize20Regular, ArrowDownload16Regular, Info16Regular } from '@fluentui/react-icons';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python';
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash';
import powershell from 'react-syntax-highlighter/dist/esm/languages/hljs/powershell';
import csharp from 'react-syntax-highlighter/dist/esm/languages/hljs/csharp';
import rust from 'react-syntax-highlighter/dist/esm/languages/hljs/rust';
import { createTmdsClient } from '../services/tmds';
import { buildCapabilityExamples } from './exampleCapabilities';

SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('powershell', powershell);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('rust', rust);

const LANGUAGES = ['python', 'curl', 'powershell', 'csharp', 'rust'] as const;

const PRISM_LANG: Record<string, string> = {
  python: 'python',
  curl: 'bash',
  powershell: 'powershell',
  csharp: 'csharp',
  rust: 'rust',
};

export interface ExampleViewerProps {
  capabilities: string[];
  capabilityMapping?: Record<string, Record<string, unknown>>;
  defaultCapability?: string;
  defaultExample?: string;
  defaultLanguage?: string;
  model?: string;
  apiPath?: string;
  getToken: () => Promise<string>;
  onSelectionChange?: (sel: { capability: string; example: string; language: string }) => void;
}

const useStyles = makeStyles({
  container: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  codeWrapper: { display: 'flex', flexDirection: 'column' },
  codeContainer: {
    background: tokens.colorNeutralBackground3,
    padding: '12px',
    borderRadius: '0 0 6px 6px',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '520px',
    fontFamily: 'Consolas, Menlo, monospace',
    fontSize: '13px',
    lineHeight: '1.4',
    tabSize: 2,
    whiteSpace: 'pre',
    margin: 0,
    '@media (max-width: 768px)': {
      fontSize: '11px',
      padding: '8px',
      maxHeight: '400px',
    },
  },
  codeContainerFullscreen: {
    maxHeight: 'none',
    flex: 1,
    borderRadius: 0,
  },
  codeToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '4px',
    padding: '4px 12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '6px 6px 0 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexWrap: 'wrap',
  },
  tabList: { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  sidebar: {
    display: 'flex', gap: tokens.spacingHorizontalL,
    '@media (max-width: 768px)': { flexDirection: 'column' },
  },
  sidebarFullscreen: {
    display: 'flex', gap: tokens.spacingHorizontalL, flex: 1, minHeight: 0,
    '@media (max-width: 768px)': { flexDirection: 'column' },
  },
  exampleList: {
    minWidth: '200px',
    maxWidth: '220px',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingRight: tokens.spacingHorizontalM,
    overflowY: 'auto',
    '@media (max-width: 768px)': {
      minWidth: '100%',
      maxWidth: '100%',
      borderRight: 'none',
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      paddingRight: 0,
      paddingBottom: tokens.spacingVerticalM,
      marginBottom: tokens.spacingVerticalM,
      maxHeight: '200px',
    },
  },
  capGroup: { marginBottom: '2px' },
  capLabel: {
    fontWeight: 600,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '2px',
    display: 'block',
  },
  exItem: {
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: tokens.fontSizeBase200,
  },
  exItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  exItemDisabled: {
    color: tokens.colorNeutralForeground4,
    cursor: 'default',
    fontStyle: 'italic' as const,
  },
  codePane: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  fullscreenOverlay: {
    position: 'fixed',
    top: '48px',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingVerticalM,
  },
  fullscreenHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
});

export default function ExampleViewer({
  capabilities,
  capabilityMapping: capabilityMappingProp,
  defaultCapability,
  defaultExample: defaultExampleProp,
  defaultLanguage: defaultLanguageProp,
  model: modelProp,
  apiPath: apiPathProp,
  getToken,
  onSelectionChange,
}: ExampleViewerProps) {
  const styles = useStyles();
  const client = useMemo(() => createTmdsClient(getToken), [getToken]);

  // Fetch mapping internally if not provided (e.g., ModelsAccess flyout)
  const internalMappingQuery = useQuery<Record<string, Record<string, unknown>>>({
    queryKey: ['examples-mapping-internal'],
    queryFn: async () => {
      const data = await client.getExamplesMapping<{ capabilities: Record<string, Record<string, unknown>> }>();
      return data?.capabilities ?? {};
    },
    enabled: !capabilityMappingProp,
    staleTime: 300_000,
  });
  const resolvedMapping = capabilityMappingProp ?? internalMappingQuery.data ?? {};

  // Derive sidebar structure from capability mapping (no hardcoded map)
  const { examples: CAPABILITY_EXAMPLES, parentOf: CAPABILITY_PARENT } = useMemo(
    () => buildCapabilityExamples(resolvedMapping),
    [resolvedMapping],
  );

  // Build the available example tree from capabilities
  const capSet = useMemo(() => new Set(capabilities.map(c => c.toLowerCase())), [capabilities]);
  const exampleTree = useMemo(() => {
    const tree: { capability: string; examples: { label: string; description?: string; example: string }[] }[] = [];
    for (const cap of capabilities) {
      // A grouped child is already listed under its parent when the parent is advertised
      const parent = CAPABILITY_PARENT[cap];
      if (parent && capSet.has(parent.toLowerCase())) continue;
      const subs = CAPABILITY_EXAMPLES[cap];
      if (subs && subs.length > 0) {
        // Filter: sub-examples with capabilityOverride only show if that capability is available
        const filtered = subs.filter(s =>
          !s.capabilityOverride || capSet.has(s.capabilityOverride.toLowerCase())
        );
        if (filtered.length > 0) {
          tree.push({ capability: cap, examples: filtered });
        }
      }
    }
    return tree;
  }, [capabilities, capSet, CAPABILITY_EXAMPLES, CAPABILITY_PARENT]);

  // Default selection: from props or first available
  const defaultSelection = useMemo(() => {
    if (defaultCapability) {
      const subs = CAPABILITY_EXAMPLES[defaultCapability];
      if (subs && subs.length > 0) {
        const ex = defaultExampleProp && subs.find(s => s.example === defaultExampleProp)
          ? defaultExampleProp : subs[0].example;
        return { capability: defaultCapability, example: ex };
      }
    }
    // Prefer chatCompletion as default if available (most commonly used)
    if (exampleTree.length > 0) {
      const chatIdx = exampleTree.findIndex(t => t.capability.toLowerCase() === 'chatcompletion');
      const pick = chatIdx >= 0 ? exampleTree[chatIdx] : exampleTree[0];
      return { capability: pick.capability, example: pick.examples[0].example };
    }
    return { capability: '', example: '' };
  }, [defaultCapability, defaultExampleProp, exampleTree]);

  const [selectedCap, setSelectedCap] = useState(defaultSelection.capability);
  const [selectedExample, setSelectedExample] = useState(defaultSelection.example);
  const [language, setLanguage] = useState<string>(defaultLanguageProp || LANGUAGES[0]);
  const [apiVersion, setApiVersion] = useState<string>('v1');
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // Resolve capabilityOverride for API calls (e.g., Router listed under chatCompletion sends capability='router')
  const resolveApiCapability = useCallback((cap: string, example: string): string => {
    const subs = CAPABILITY_EXAMPLES[cap];
    if (subs) {
      const entry = subs.find(s => s.example === example);
      if (entry?.capabilityOverride) return entry.capabilityOverride;
    }
    return cap;
  }, [CAPABILITY_EXAMPLES]);
  const isAvailableForVersion = useCallback((cap: string, example: string, ver: string): boolean => {
    const subs = CAPABILITY_EXAMPLES[cap];
    if (!subs) return true;
    const entry = subs.find(s => s.example === example);
    if (!entry?.versions) return true; // no restriction = available everywhere
    return entry.versions.includes(ver);
  }, [CAPABILITY_EXAMPLES]);

  const fetchExample = useCallback(
    async (cap: string, example: string, lang: string, ver: string) => {
      if (!cap || !example) return;
      setLoading(true);
      setError(null);
      setCode(null);
      const apiCap = resolveApiCapability(cap, example);
      // Pass model/apiPath to server for substitution
      const subOpts = { model: modelProp, apiPath: apiPathProp };

      try {
        const res = await client.postExample({
          capability: apiCap,
          example,
          language: lang,
          apiVersion: ver,
          display: 'online',
          ...subOpts,
        });
        const text = res.text ?? '';
        if (text && !text.includes('"api-version not available"')) {
          setCode(text);
          setLoading(false);
          return;
        }
      } catch { /* fall through */ }

      // If requested version failed, try the other
      const fallbackVer = ver === 'v1' ? 'fallback' : 'v1';
      try {
        const res = await client.postExample({
          capability: apiCap,
          example,
          language: lang,
          apiVersion: fallbackVer,
          display: 'online',
          ...subOpts,
        });
        const text = res.text ?? '';
        if (text && !text.includes('"api-version not available"')) {
          setCode(text);
          setApiVersion(fallbackVer);
          setLoading(false);
          return;
        }
      } catch { /* fall through */ }

      // Try without version
      try {
        const res = await client.postExample({ capability: apiCap, example, language: lang, display: 'online', ...subOpts });
        setCode(res.text ?? 'No example available.');
      } catch {
        setError(`No example found for "${example}" in ${lang}.`);
      } finally {
        setLoading(false);
      }
    },
    [client, resolveApiCapability, modelProp, apiPathProp],
  );

  useEffect(() => {
    if (selectedCap && selectedExample && isAvailableForVersion(selectedCap, selectedExample, apiVersion)) {
      void fetchExample(selectedCap, selectedExample, language, apiVersion);
    } else if (selectedCap && selectedExample) {
      setCode(null);
      setError(null);
    }
  }, [selectedCap, selectedExample, language, apiVersion, fetchExample, isAvailableForVersion]);

  // Code is returned with substitutions already applied server-side
  const displayCode = code;

  // Notify parent of selection changes for URL deep-linking
  useEffect(() => {
    if (selectedCap && selectedExample && onSelectionChange) {
      onSelectionChange({ capability: selectedCap, example: selectedExample, language });
    }
  }, [selectedCap, selectedExample, language, onSelectionChange]);

  const handleCopy = useCallback(async () => {
    if (!displayCode) return;
    try {
      await navigator.clipboard.writeText(displayCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setError('Copy failed'); }
  }, [displayCode]);

  const handleDownload = useCallback(() => {
    if (!displayCode) return;
    const ext = language === 'python' ? 'py' : language === 'powershell' ? 'ps1' : language === 'csharp' ? 'cs' : language === 'rust' ? 'rs' : 'sh';
    const blob = new Blob([displayCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedExample.replace(/\//g, '_')}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }, [displayCode, language, selectedExample]);

  if (exampleTree.length === 0) {
    if (!capabilityMappingProp && internalMappingQuery.isLoading) {
      return <Spinner size="small" label="Loading examples…" />;
    }
    return (
      <Text size={200} style={{ color: tokens.colorNeutralForeground3, padding: tokens.spacingVerticalM }}>
        No examples available for this model&apos;s capabilities.
      </Text>
    );
  }

  return (
    <div className={styles.container}>
      {/* Language tabs */}
      <TabList
        className={styles.tabList}
        selectedValue={language}
        onTabSelect={(_e, data) => setLanguage(data.value as string)}
        size="small"
      >
        {LANGUAGES.map((l) => (
          <Tab key={l} value={l}>
            {l === 'curl' ? 'cURL' : l === 'csharp' ? 'C#' : l === 'rust' ? 'Rust' : l.charAt(0).toUpperCase() + l.slice(1)}
          </Tab>
        ))}
      </TabList>

      {/* API version toggle with explanation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text size={200} weight="semibold" style={{ color: tokens.colorNeutralForeground3 }}>API:</Text>
        <TabList
          selectedValue={apiVersion}
          onTabSelect={(_e, data) => setApiVersion(data.value as string)}
          size="small"
          appearance="subtle"
        >
          <Tab value="v1">v1</Tab>
          <Tab value="fallback">Fallback</Tab>
        </TabList>
        <Tooltip
          content={
            <div style={{ maxWidth: 320 }}>
              <div style={{ marginBottom: 4 }}><strong>v1</strong> — OpenAI-compatible endpoint using the <code>/openai/v1/</code> path.
                Model is specified in the request body. <a href="https://learn.microsoft.com/en-us/azure/ai-services/openai/api-version-deprecation" target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>Learn more</a></div>
              <div><strong>Fallback</strong> — Azure OpenAI deployment-based endpoint using <code>/openai/deployments/{'{'}<em>model</em>{'}'}/</code> path with an explicit <code>api-version</code> query parameter. <a href="https://learn.microsoft.com/en-us/azure/ai-services/openai/reference" target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>Learn more</a></div>
            </div>
          }
          relationship="description"
          positioning="below"
        >
          <Button size="small" appearance="subtle" icon={<Info16Regular />} aria-label="API version info" style={{ minWidth: 'auto', padding: '0 2px' }} />
        </Tooltip>
      </div>

      {/* Example list + code pane side by side */}
      <div className={styles.sidebar}>
        {/* Example tree */}
        <div className={styles.exampleList}>
          {exampleTree.map(({ capability, examples }) => (
            <div key={capability} className={styles.capGroup}>
              <span className={styles.capLabel}>{capability}</span>
              {examples.map((ex) => {
                const isActive = selectedCap === capability && selectedExample === ex.example;
                const available = isAvailableForVersion(capability, ex.example, apiVersion);
                return (
                  <div
                    key={ex.example}
                    className={`${styles.exItem} ${isActive ? styles.exItemActive : ''} ${!available ? styles.exItemDisabled : ''}`}
                    onClick={() => { if (available) { setSelectedCap(capability); setSelectedExample(ex.example); } }}
                    role="button"
                    tabIndex={available ? 0 : -1}
                    aria-disabled={!available}
                    onKeyDown={(e) => { if (e.key === 'Enter' && available) { setSelectedCap(capability); setSelectedExample(ex.example); } }}
                    title={!available ? `Not available for ${apiVersion}` : ex.description || undefined}
                  >
                    {ex.label}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Code pane */}
        <div className={styles.codePane}>
          {!isAvailableForVersion(selectedCap, selectedExample, apiVersion) && !loading && (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, padding: tokens.spacingVerticalM }}>
              This example is not available for the <strong>{apiVersion}</strong> API version.
              {(() => {
                const subs = CAPABILITY_EXAMPLES[selectedCap];
                const entry = subs?.find(s => s.example === selectedExample);
                const vers = entry?.versions;
                return vers?.length ? <> Try switching to <strong>{vers[0]}</strong>.</> : null;
              })()}
            </Text>
          )}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner size="tiny" />
              <Text size={200}>Loading…</Text>
            </div>
          )}
          {error && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>}

          {displayCode && !loading && (
            <div className={styles.codeWrapper}>
              <div className={styles.codeToolbar}>
                <Button appearance="subtle" size="small" icon={<Copy16Regular />} onClick={handleCopy} title="Copy">
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button appearance="subtle" size="small" icon={<ArrowDownload16Regular />} onClick={handleDownload} title="Download">
                  Save
                </Button>
                <Button appearance="subtle" size="small" icon={<ArrowMaximize20Regular />} onClick={() => setFullscreen(true)} title="Full screen" />
              </div>
              <SyntaxHighlighter
                language={PRISM_LANG[language] || 'text'}
                style={docco}
                className={styles.codeContainer}
                customStyle={{ margin: 0, background: undefined }}
                showLineNumbers
              >{displayCode}</SyntaxHighlighter>
            </div>
          )}

          {!displayCode && !loading && !error && (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Select an example to view.</Text>
          )}
        </div>
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && displayCode && (
        <div className={styles.fullscreenOverlay}>
          <div className={styles.fullscreenHeader}>
            <Text weight="semibold">{selectedCap} — {selectedExample} ({language})</Text>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button appearance="subtle" size="small" icon={<Copy16Regular />} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</Button>
              <Button appearance="subtle" size="small" icon={<ArrowDownload16Regular />} onClick={handleDownload}>Save</Button>
              <Button appearance="subtle" size="small" icon={<ArrowMinimize20Regular />} onClick={() => setFullscreen(false)}>Exit</Button>
            </div>
          </div>
          <SyntaxHighlighter
            language={PRISM_LANG[language] || 'text'}
            style={docco}
            className={`${styles.codeContainer} ${styles.codeContainerFullscreen}`}
            customStyle={{ margin: 0, background: undefined }}
            showLineNumbers
          >{displayCode}</SyntaxHighlighter>
        </div>
      )}
    </div>
  );
}
